import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { jobService } from '../../services/jobService';
import { Job } from '../../types/job';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface JobDetailProps {
  jobId:              string;
  onEdit:             (job: Job) => void;
  onBack:             () => void;
  onConvertToInvoice: (job: Job) => void;
}

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bgBase:        '#f4efe2',
  bgCard:        '#ffffff',
  bgElevated:    '#faf7ee',
  gold:          '#d97706',
  goldLight:     '#fbbf24',
  goldHover:     '#b45309',
  textPrimary:   '#1c2033',
  textSecondary: '#5b5a52',
  textMuted:     '#8a8778',
  borderNormal:  'rgba(92,78,51,0.14)',
  borderSubtle:  'rgba(92,78,51,0.22)',
  fontHeading:   "'Inter', sans-serif",
  fontLabel:     "'Inter', sans-serif",
  fontBody:      "'Inter', sans-serif",
};

// ─── Job status badge ───────────────────────────────────────────────────────────
const jobStatusBadge = (status: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string; border: string }> = {
    ACTIVE:    { bg: 'rgba(90,201,140,0.12)',  color: '#5ac98c', border: 'rgba(90,201,140,0.25)'  },
    COMPLETED: { bg: 'rgba(90,140,201,0.12)',  color: '#5a8cc9', border: 'rgba(90,140,201,0.25)'  },
    PENDING:   { bg: 'rgba(92,78,51,0.22)',  color: '#d97706', border: 'rgba(92,78,51,0.35)'  },
    ON_HOLD:   { bg: 'rgba(92,78,51,0.22)',  color: '#d97706', border: 'rgba(92,78,51,0.35)'  },
    CANCELLED: { bg: 'rgba(80,80,80,0.15)',    color: '#555570', border: 'rgba(80,80,80,0.25)'    },
  };
  const s = map[status] ?? { bg: 'rgba(120,120,140,0.15)', color: '#9a9ab0', border: 'rgba(120,120,140,0.25)' };
  return {
    display:       'inline-flex',
    alignItems:    'center',
    padding:       '3px 12px',
    borderRadius:  20,
    fontSize:      10,
    fontFamily:    C.fontLabel,
    letterSpacing: '0.18em',
    fontWeight:    600,
    background:    s.bg,
    color:         s.color,
    border:        `1px solid ${s.border}`,
    textTransform: 'uppercase' as const,
  };
};

// ─── Bill status badge (compact) ───────────────────────────────────────────────
const billStatusBadge = (status: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string }> = {
    DRAFT:     { bg: 'rgba(120,120,140,0.12)', color: '#9a9ab0' },
    PENDING:   { bg: 'rgba(92,78,51,0.22)',  color: '#d97706' },
    APPROVED:  { bg: 'rgba(90,140,201,0.12)',  color: '#5a8cc9' },
    PAID:      { bg: 'rgba(90,201,140,0.12)',  color: '#5ac98c' },
    OVERDUE:   { bg: 'rgba(201,90,90,0.12)',   color: '#c95a5a' },
    CANCELLED: { bg: 'rgba(80,80,80,0.15)',    color: '#555570' },
  };
  const s = map[status] ?? map.DRAFT;
  return {
    display:       'inline-block',
    padding:       '2px 8px',
    borderRadius:  3,
    fontSize:      9,
    fontFamily:    C.fontLabel,
    letterSpacing: '0.14em',
    fontWeight:    600,
    background:    s.bg,
    color:         s.color,
    textTransform: 'uppercase' as const,
  };
};

// ─── Shared label / value styles ────────────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  fontFamily:    C.fontLabel,
  fontSize:      9,
  letterSpacing: '0.2em',
  color:         C.textMuted,
  textTransform: 'uppercase',
  marginBottom:  3,
};

const valueStyle: React.CSSProperties = {
  fontFamily: C.fontBody,
  fontSize:   12,
  color:      C.textPrimary,
  lineHeight: 1.5,
};

// ─── Section header strip ───────────────────────────────────────────────────────
const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <div style={{
    display:      'flex',
    alignItems:   'center',
    gap:          8,
    padding:      '10px 22px',
    borderBottom: `1px solid ${C.borderNormal}`,
    background:   'rgba(241,245,249,1)',
  }}>
    <div style={{ width: 3, height: 14, background: C.gold, borderRadius: 2 }} />
    <span style={{
      fontFamily:    C.fontLabel,
      fontSize:      9,
      letterSpacing: '0.2em',
      color:         C.textMuted,
      textTransform: 'uppercase',
    }}>
      {label}
    </span>
  </div>
);

// ─── Elevated card wrapper ──────────────────────────────────────────────────────
const elevatedCard: React.CSSProperties = {
  background:   C.bgElevated,
  border:       `1px solid ${C.borderSubtle}`,
  borderRadius: 6,
  overflow:     'hidden',
};

export const JobDetail: React.FC<JobDetailProps> = ({ jobId, onEdit, onBack, onConvertToInvoice }) => {
  const navigate = useNavigate();
  const [hoveredBtn, setHoveredBtn] = React.useState<string | null>(null);

  const { data: job, isLoading, error } = useQuery({
    queryKey: ['job', jobId],
    queryFn:  () => jobService.getJob(jobId),
  });

  // ── Loading state ─────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ padding: 24, background: C.bgBase, minHeight: '100vh' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{
              height:       i === 1 ? 28 : 16,
              background:   C.bgElevated,
              borderRadius: 4,
              marginBottom: 16,
              width:        i === 1 ? '25%' : i === 2 ? '75%' : '50%',
              opacity:      0.6,
            }} />
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────────
  if (error || !job) {
    return (
      <div style={{
        padding:        24,
        background:     C.bgBase,
        minHeight:      '100vh',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: C.fontBody, color: '#c95a5a', marginBottom: 16 }}>
            Error loading job details. Please try again.
          </p>
          <button
            onClick={onBack}
            style={{
              fontFamily:    C.fontLabel,
              fontSize:      9,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color:         C.gold,
              background:    'transparent',
              border:        `1px solid ${C.borderSubtle}`,
              borderRadius:  4,
              padding:       '8px 18px',
              cursor:        'pointer',
            }}
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  const getProgressPercentage = () => {
    if (job.status === 'COMPLETED') return 100;
    if (job.status === 'CANCELLED') return 0;
    if (!job.estimatedValue || job.estimatedValue === 0) return 0;
    return Math.min(100, (job.actualValue / job.estimatedValue) * 100);
  };

  // ── Action button helper ──────────────────────────────────────────────────────
  const actionBtn = (
    id:      string,
    label:   string,
    onClick: () => void,
    variant: 'gold' | 'ghost' | 'green',
  ) => {
    const variantStyles: Record<string, React.CSSProperties> = {
      gold:  { background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgCard,        border: 'none' },
      ghost: { background: 'transparent',                                        color: C.textSecondary,  border: `1px solid ${C.borderSubtle}` },
      green: { background: 'transparent',                                        color: '#5ac98c',        border: '1px solid rgba(90,201,140,0.3)' },
    };
    const hovered     = hoveredBtn === id;
    const hoverAdjust: React.CSSProperties = hovered
      ? variant === 'gold'
        ? { background: `linear-gradient(135deg, ${C.goldHover}, ${C.gold})`, transform: 'translateY(-1px)' }
        : { background: C.bgElevated }
      : {};
    return (
      <button
        key={id}
        onClick={onClick}
        onMouseEnter={() => setHoveredBtn(id)}
        onMouseLeave={() => setHoveredBtn(null)}
        style={{
          fontFamily:    C.fontLabel,
          fontSize:      9,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          padding:       '8px 18px',
          borderRadius:  4,
          cursor:        'pointer',
          transition:    'all 0.2s ease',
          whiteSpace:    'nowrap',
          ...variantStyles[variant],
          ...hoverAdjust,
        }}
      >
        {label}
      </button>
    );
  };

  const hasConvertibleBills = job.bills && job.bills.some(bill => bill.status !== 'CANCELLED');
  const progress            = getProgressPercentage();

  return (
    <div style={{ padding: '24px 16px', background: C.bgBase, minHeight: '100vh' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>

        {/* ── Back Link ──────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 20 }}>
          <button
            onClick={onBack}
            onMouseEnter={() => setHoveredBtn('back')}
            onMouseLeave={() => setHoveredBtn(null)}
            style={{
              display:       'inline-flex',
              alignItems:    'center',
              gap:           6,
              background:    'transparent',
              border:        'none',
              color:         hoveredBtn === 'back' ? C.gold : C.textMuted,
              fontFamily:    C.fontBody,
              fontSize:      12,
              letterSpacing: '0.06em',
              cursor:        'pointer',
              padding:       0,
              transition:    'color 0.2s',
            }}
          >
            <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Jobs
          </button>
        </div>

        {/* ── Job Card ───────────────────────────────────────────────────────── */}
        <div style={{
          background:   C.bgCard,
          border:       `1px solid ${C.borderSubtle}`,
          borderRadius: 8,
          overflow:     'hidden',
        }}>

          {/* Card Header */}
          <div style={{
            background:   `linear-gradient(180deg, ${C.bgElevated} 0%, ${C.bgCard} 100%)`,
            borderBottom: `2px solid ${C.gold}`,
            padding:      '28px 32px',
          }}>
            <div style={{
              display:        'flex',
              justifyContent: 'space-between',
              alignItems:     'flex-start',
              flexWrap:       'wrap',
              gap:            16,
            }}>
              {/* Title block */}
              <div>
                <h1 style={{
                  fontFamily:    C.fontHeading,
                  fontSize:      36,
                  fontWeight:    300,
                  color:         C.textPrimary,
                  margin:        '0 0 4px 0',
                  letterSpacing: '0.03em',
                  lineHeight:    1.15,
                }}>
                  {job.jobName}
                </h1>
                <div style={{
                  width:      48,
                  height:     1,
                  background: `linear-gradient(to right, ${C.gold}, transparent)`,
                  marginBottom: 8,
                }} />
                <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>
                  Job #{job.jobNumber}
                </p>
              </div>

              {/* Status + Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={jobStatusBadge(job.status)}>
                  {job.status.replace('_', ' ')}
                </span>
                {actionBtn('edit', 'Edit Job', () => onEdit(job), 'gold')}
                {actionBtn(
                  'create-inv',
                  'Create Invoice',
                  () => navigate('/invoices', { state: { createForJob: { jobId: job.id, customerId: job.customerId } } }),
                  'ghost',
                )}
                {hasConvertibleBills &&
                  actionBtn('convert', 'Convert to Invoice', () => onConvertToInvoice(job), 'green')
                }
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div style={{ padding: 28 }}>
            <div style={{
              display:             'grid',
              gridTemplateColumns: '1fr 340px',
              gap:                 28,
              marginBottom:        32,
            }}>

              {/* Left: Customer + Description */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Customer Information */}
                <div style={{ ...elevatedCard }}>
                  <SectionHeader label="Customer Information" />
                  <div style={{ padding: 22 }}>
                    <div style={{
                      display:             'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap:                 16,
                    }}>
                      <div>
                        <div style={labelStyle}>Customer Name</div>
                        <div style={valueStyle}>{job.customer?.name}</div>
                      </div>
                      {job.customer?.email && (
                        <div>
                          <div style={labelStyle}>Email</div>
                          <div style={valueStyle}>{job.customer.email}</div>
                        </div>
                      )}
                      {job.customer?.phone && (
                        <div>
                          <div style={labelStyle}>Phone</div>
                          <div style={valueStyle}>{job.customer.phone}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {job.description && (
                  <div style={{ ...elevatedCard }}>
                    <SectionHeader label="Description" />
                    <div style={{ padding: 22 }}>
                      <p style={{
                        fontFamily: C.fontBody,
                        fontSize:   12,
                        color:      C.textSecondary,
                        lineHeight: 1.7,
                        margin:     0,
                      }}>
                        {job.description}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Statistics + Timeline */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Job Statistics */}
                <div style={{ ...elevatedCard }}>
                  <SectionHeader label="Job Statistics" />
                  <div style={{ padding: 22 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                      {/* Progress bar */}
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{
                            fontFamily:    C.fontLabel,
                            fontSize:      9,
                            letterSpacing: '0.15em',
                            color:         C.textMuted,
                            textTransform: 'uppercase',
                          }}>Progress</span>
                          <span style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary }}>
                            {progress.toFixed(0)}%
                          </span>
                        </div>
                        <div style={{
                          width:        '100%',
                          height:       6,
                          background:   C.bgBase,
                          borderRadius: 3,
                          overflow:     'hidden',
                        }}>
                          <div style={{
                            height:     '100%',
                            width:      `${progress}%`,
                            background: `linear-gradient(to right, ${C.gold}, ${C.goldLight})`,
                            borderRadius: 3,
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                      </div>

                      {/* Estimated value */}
                      {job.estimatedValue && (
                        <div>
                          <div style={labelStyle}>Estimated Value</div>
                          <div style={{
                            fontFamily:    C.fontHeading,
                            fontSize:      20,
                            fontWeight:    300,
                            color:         C.goldLight,
                            letterSpacing: '0.02em',
                          }}>
                            {formatCurrency(job.estimatedValue)}
                          </div>
                        </div>
                      )}

                      {/* Actual value */}
                      <div>
                        <div style={labelStyle}>Actual Value</div>
                        <div style={{
                          fontFamily:    C.fontHeading,
                          fontSize:      20,
                          fontWeight:    300,
                          color:         C.textPrimary,
                          letterSpacing: '0.02em',
                        }}>
                          {formatCurrency(job.actualValue)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div style={{ ...elevatedCard }}>
                  <SectionHeader label="Timeline" />
                  <div style={{ padding: 22 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {job.startDate && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{
                            fontFamily:    C.fontLabel,
                            fontSize:      9,
                            letterSpacing: '0.15em',
                            color:         C.textMuted,
                            textTransform: 'uppercase',
                          }}>Start Date</span>
                          <span style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary }}>
                            {formatDate(job.startDate)}
                          </span>
                        </div>
                      )}
                      {job.endDate && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{
                            fontFamily:    C.fontLabel,
                            fontSize:      9,
                            letterSpacing: '0.15em',
                            color:         C.textMuted,
                            textTransform: 'uppercase',
                          }}>End Date</span>
                          <span style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary }}>
                            {formatDate(job.endDate)}
                          </span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{
                          fontFamily:    C.fontLabel,
                          fontSize:      9,
                          letterSpacing: '0.15em',
                          color:         C.textMuted,
                          textTransform: 'uppercase',
                        }}>Created</span>
                        <span style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary }}>
                          {formatDate(job.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Bills & Invoices ──────────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>

              {/* Bills */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <h3 style={{
                    fontFamily:    C.fontHeading,
                    fontSize:      20,
                    fontWeight:    300,
                    color:         C.textPrimary,
                    margin:        0,
                    letterSpacing: '0.03em',
                  }}>
                    Bills
                  </h3>
                  <span style={{ fontFamily: C.fontBody, fontSize: 11, color: C.textMuted }}>
                    {job.bills?.length || 0} bill{(job.bills?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{
                  width:        32,
                  height:       1,
                  background:   `linear-gradient(to right, ${C.gold}, transparent)`,
                  marginBottom: 14,
                }} />

                {job.bills?.length ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {job.bills.map((bill) => (
                      <div key={bill.id} style={{
                        background:   C.bgElevated,
                        border:       `1px solid ${C.borderSubtle}`,
                        borderRadius: 6,
                        padding:      16,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <p style={{
                              fontFamily: C.fontBody,
                              fontSize:   12,
                              color:      C.textPrimary,
                              margin:     '0 0 2px 0',
                              fontWeight: 500,
                            }}>
                              {bill.billNumber}
                            </p>
                            <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, margin: '0 0 2px 0' }}>
                              {bill.vendor.name}
                            </p>
                            <p style={{ fontFamily: C.fontBody, fontSize: 11, color: C.textMuted, margin: 0 }}>
                              {formatDate(bill.billDate)}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{
                              fontFamily: C.fontHeading,
                              fontSize:   16,
                              fontWeight: 300,
                              color:      C.goldLight,
                              margin:     '0 0 4px 0',
                            }}>
                              {formatCurrency(bill.total)}
                            </p>
                            <span style={billStatusBadge(bill.status)}>{bill.status}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign:    'center',
                    padding:      '32px 16px',
                    border:       `2px dashed ${C.borderSubtle}`,
                    borderRadius: 6,
                    background:   C.bgElevated,
                  }}>
                    <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>
                      No bills yet
                    </p>
                  </div>
                )}
              </div>

              {/* Invoices */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <h3 style={{
                    fontFamily:    C.fontHeading,
                    fontSize:      20,
                    fontWeight:    300,
                    color:         C.textPrimary,
                    margin:        0,
                    letterSpacing: '0.03em',
                  }}>
                    Invoices
                  </h3>
                  <span style={{ fontFamily: C.fontBody, fontSize: 11, color: C.textMuted }}>
                    {job.invoices?.length || 0} invoice{(job.invoices?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{
                  width:        32,
                  height:       1,
                  background:   `linear-gradient(to right, ${C.gold}, transparent)`,
                  marginBottom: 14,
                }} />

                {job.invoices?.length ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {job.invoices.map((invoice) => (
                      <div key={invoice.id} style={{
                        background:   C.bgElevated,
                        border:       `1px solid ${C.borderSubtle}`,
                        borderRadius: 6,
                        padding:      16,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <p style={{
                              fontFamily: C.fontBody,
                              fontSize:   12,
                              color:      C.textPrimary,
                              margin:     '0 0 2px 0',
                              fontWeight: 500,
                            }}>
                              {invoice.invoiceNumber}
                            </p>
                            <p style={{ fontFamily: C.fontBody, fontSize: 11, color: C.textMuted, margin: 0 }}>
                              {formatDate(invoice.issueDate)}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{
                              fontFamily: C.fontHeading,
                              fontSize:   16,
                              fontWeight: 300,
                              color:      C.goldLight,
                              margin:     '0 0 4px 0',
                            }}>
                              {formatCurrency(invoice.total)}
                            </p>
                            <span style={{
                              display:       'inline-block',
                              padding:       '2px 8px',
                              borderRadius:  3,
                              fontSize:      9,
                              fontFamily:    C.fontLabel,
                              letterSpacing: '0.14em',
                              fontWeight:    600,
                              background:    'rgba(90,201,140,0.12)',
                              color:         '#5ac98c',
                              textTransform: 'uppercase',
                            }}>
                              {invoice.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign:    'center',
                    padding:      '32px 16px',
                    border:       `2px dashed ${C.borderSubtle}`,
                    borderRadius: 6,
                    background:   C.bgElevated,
                  }}>
                    <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>
                      No invoices yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
